import {FC, SVGProps} from "react";
import slugify from "@sindresorhus/slugify";
import {DibDocument, DibElementChild, FileType, ResourceType} from "./types";
import {TOPIC_TYPE_PATHS} from "./paths";
import FileTypePdf from "../../assets/file-type-pdf.svg?react";
import FileTypeDoc from "../../assets/file-type-doc.svg?react";
import FileTypeXls from "../../assets/file-type-xls.svg?react";
import FileTypePpt from "../../assets/file-type-ppt.svg?react";

export const validFileTypes = [
    ResourceType.pdf,
    ResourceType.microsoftWord,
    ResourceType.microsoftWordOld,
    ResourceType.microsoftExcel,
    ResourceType.microsoftExcelOld,
    ResourceType.microsoftExcelTemplate,
    ResourceType.microsoftExcelMacroEnabledTemplate,
    ResourceType.microsoftExcelMacroEnabled,
    ResourceType.microsoftPowerpoint,
];

export const isValidFileType = (
    resourceTypeId?: ResourceType
): resourceTypeId is FileType => {
    if (!resourceTypeId) return false;

    // Sometimes the ResourceTypeId is a number.... :(
    const resourceTypeIdString = resourceTypeId.toString() as ResourceType; // bc fake enums in TS/JS...
    return validFileTypes.includes(resourceTypeIdString);
};

export const fileIconMap: Record<string, FC<SVGProps<SVGSVGElement>>> = {
    [ResourceType.pdf]: FileTypePdf,
    [ResourceType.microsoftWord]: FileTypeDoc,
    [ResourceType.microsoftWordOld]: FileTypeDoc,
    [ResourceType.microsoftExcel]: FileTypeXls,
    [ResourceType.microsoftExcelOld]: FileTypeXls,
    [ResourceType.microsoftExcelTemplate]: FileTypeXls,
    [ResourceType.microsoftExcelMacroEnabledTemplate]: FileTypeXls,
    [ResourceType.microsoftExcelMacroEnabled]: FileTypeXls,
    [ResourceType.microsoftPowerpoint]: FileTypePpt,
};

export const fileExtensionMap: Record<string, string> = {
    [ResourceType.pdf]: ".pdf",
    [ResourceType.microsoftWord]: ".docx",
    [ResourceType.microsoftWordOld]: ".doc",
    [ResourceType.microsoftExcel]: ".xlsx",
    [ResourceType.microsoftExcelOld]: ".xls",
    [ResourceType.microsoftExcelTemplate]: ".xltx",
    [ResourceType.microsoftExcelMacroEnabledTemplate]: ".xltm",
    [ResourceType.microsoftExcelMacroEnabled]: ".xlsm",
    [ResourceType.microsoftPowerpoint]: ".pptx",
};

export const fileColorMap: Record<string, string> = {
    [ResourceType.pdf]: "#d43731",
    [ResourceType.microsoftWord]: "#2a5797",
    [ResourceType.microsoftWordOld]: "#2a5797",
    [ResourceType.microsoftExcel]: "#1f7244",
    [ResourceType.microsoftExcelOld]: "#1f7244",
    [ResourceType.microsoftExcelTemplate]: "#1f7244",
    [ResourceType.microsoftExcelMacroEnabledTemplate]: "#1f7244",
    [ResourceType.microsoftExcelMacroEnabled]: "#1f7244",
    [ResourceType.microsoftPowerpoint]: "#d34724",
};

/** Build an internal topic URL from topic metadata */
export const buildTopicUrl = (
    topicTypeId: string,
    topicName: string,
    topicId: string,
    segmentId?: string
): string => {
    const typePath = TOPIC_TYPE_PATHS[topicTypeId] ?? "ikke-implementert";
    const slug = slugify(topicName);
    const base = `/${typePath}/${slug}/${topicId}`;
    return segmentId ? `${base}/segment/${segmentId}` : base;
};

/** Walk the elements tree and extract plain text from an element and its children */
export const extractText = (
    elements: DibDocument["elements"],
    id: string
): string => {
    const element = elements[id];
    if (!element) return "";

    return (element.children || ([] as DibElementChild[]))
        .map((child) => {
            if ("text" in child) return child.text;
            return extractText(elements, child.id);
        })
        .join(" ");
};
